export interface IRandomSource {
  nextDouble(): number; // [0, 1)
}

export class JsRandomSource implements IRandomSource {
  nextDouble(): number {
    return Math.random();
  }
}

export class SeededRandomSource implements IRandomSource {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  nextDouble(): number {
    let t = (this.state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
}

export class Random {
  private source: IRandomSource;

  constructor(seed?: number) {
    this.source = seed === undefined ? new JsRandomSource() : new SeededRandomSource(seed);
  }

  next(min?: number, max?: number): number {
    if (min === undefined) {
      return Math.floor(this.source.nextDouble() * 0x7fffffff);
    }

    if (max === undefined) {
      return Math.floor(this.source.nextDouble() * min);
    }

    if (max <= min) {
      throw new Error('max must be greater than min');
    }

    return min + Math.floor(this.source.nextDouble() * (max - min));
  }

  nextDouble(): number {
    return this.source.nextDouble();
  }

  nextBoolean(): boolean {
    return this.nextDouble() < 0.5;
  }

  nextInt32(): number {
    return this.next(0x7fffffff);
  }

  NN(count: number, min: number, max: number, condition?: (arr: number[]) => boolean): number[] {
    if (!Number.isInteger(count) || count < 0) {
      throw new Error('count must be a non-negative integer');
    }
    if (!Number.isInteger(min) || !Number.isInteger(max)) {
      throw new Error('min and max must be integers');
    }
    if (max < min) {
      throw new Error('max must be >= min');
    }

    // this.next(min, maxExclusive) expects maxExclusive > min, so use max + 1
    const nextInclusive = (a: number, b: number) => this.next(a, b + 1);

    while (true) {
      const arr: number[] = new Array(count);
      for (let i = 0; i < count; i++) {
        arr[i] = nextInclusive(min, max);
      }
      if (!condition || condition(arr)) return arr;
      // Note: if condition is impossible this will loop forever (same as original C#).
    }
  }

  digitsInt32(count: number): number {
    if (!Number.isInteger(count) || count <= 0) {
      throw new Error('count must be an integer greater than 0');
    }

    let result = this.next(1, 10); // yields 1..9
    for (let i = 1; i < count; i++) {
      result = result * 10 + this.next(0, 10); // yields 0..9
    }
    return result;
  }

  digitsInt64(count: number): bigint {
    if (!Number.isInteger(count) || count <= 0) {
      throw new Error('count must be an integer greater than 0');
    }

    let result = BigInt(this.next(1, 10)); // 1..9
    for (let i = 1; i < count; i++) {
      result = result * 10n + BigInt(this.next(0, 10)); // 0..9
    }
    return result;
  }

  nextElement<T>(arr: T[]): T {
    if (arr.length === 0) {
      throw new Error('Array is empty');
    }
    return arr[this.next(arr.length)];
  }
}
